import { useState } from 'react'
import { Check, Copy, Maximize2, Pause, Play, Square, TriangleAlert } from 'lucide-react'
import { useRecordingStore, formatElapsed } from '@/stores/recording'
import { LedgerMark } from '@/components/shared/ClaroLogo'
import type { ScribeSession } from '@/hooks/useScribeSession'
import { jLocale } from '@/lib/jurisdiction'

/**
 * The contents of the pop-out scribe window.
 *
 * Two sizes, because the two moments have opposite needs. DURING the consult
 * the clinician is looking at the patient, not the screen — they need a stop
 * button and proof it is still recording, and nothing else, so the window is
 * small enough to sit in dead space over a single-screen EMR. AFTER it, the
 * patient interaction is over and screen space is suddenly cheap, so it grows
 * to show the note. Copy then collapses it again in the same action, which is
 * what keeps the EMR unobstructed at the moment they paste.
 *
 * Pure presentation over `useRecordingStore` and `useScribeSession`. It owns
 * no capture state, so it can be closed and reopened mid-consult without
 * touching the recording.
 */

interface ScribePopoutProps {
  session: ScribeSession
  /**
   * Grow the window. MUST be reached from a click — a document PiP window
   * refuses resizeTo() without user activation.
   */
  onExpand?: () => void
  /** Shrink the window back to the compact bar. Also gesture-only. */
  onShrink?: () => void
  /** Forget the finished note after a copy. */
  onCollapse?: () => void
}

export function ScribePopout({ session, onExpand, onShrink, onCollapse }: ScribePopoutProps) {
  const { phase, elapsed, noteText, captureInterruptedAt } = session
  const pauseRecording = useRecordingStore((s) => s.pauseRecording)
  const resumeRecording = useRecordingStore((s) => s.resumeRecording)
  const stopRecording = useRecordingStore((s) => s.stopRecording)

  // The one gesture that reliably lands: pressing Stop here is a click in THIS
  // window, at the moment the consult ends. Grow first, then stop — the note
  // arrives into a window that is already the right size, and the growth costs
  // nothing because the patient interaction is over.
  const handleStop = () => {
    onExpand?.()
    void stopRecording()
  }

  if (phase === 'recording' || phase === 'paused') {
    return (
      <div className="h-screen flex items-center justify-between px-4 py-3 bg-rec/5">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="w-2 h-2 rounded-full bg-rec recording-pulse shrink-0" />
          <span className="text-sm font-medium text-ink">
            {phase === 'paused' ? 'Paused' : 'Listening'}
          </span>
          <span className="text-sm text-ink-secondary tabular-nums">
            {formatElapsed(elapsed)}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {phase === 'recording' ? (
            <button
              onClick={() => pauseRecording()}
              className="p-1.5 rounded-lg hover:bg-claro-100 text-ink-secondary transition-colors"
              title="Pause"
              aria-label="Pause recording"
            >
              <Pause className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => resumeRecording()}
              className="p-1.5 rounded-lg hover:bg-claro-100 text-claro-700 transition-colors"
              title="Resume"
              aria-label="Resume recording"
            >
              <Play className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={handleStop}
            className="p-1.5 rounded-lg hover:bg-record-soft text-record transition-colors"
            title="Stop & save"
            aria-label="Stop and save recording"
          >
            <Square className="w-4 h-4" />
          </button>
        </div>
      </div>
    )
  }

  if (phase === 'processing') {
    return (
      <div className="h-screen flex items-center px-4 py-3 bg-claro-50">
        <div className="flex items-center gap-2.5">
          <LedgerMark motion="writing" size={16} className="text-claro-600" />
          <span className="text-sm font-medium text-ink">Generating note…</span>
        </div>
      </div>
    )
  }

  if (phase === 'failed') {
    return (
      <div className="h-screen overflow-hidden flex flex-col bg-surface">
        <PopoutHeader tone="error" title="Couldn't generate this note" />
        <div className="grow min-h-0 overflow-y-auto px-4 py-4">
          <p className="text-sm text-ink-secondary leading-relaxed">
            The recording is safe. Open the note in Claro to see what happened and try again.
          </p>
        </div>
      </div>
    )
  }

  if (phase === 'ready') {
    return (
      <div className="h-screen overflow-hidden flex flex-col bg-surface">
        <PopoutHeader tone="ready" title="Note ready" onExpand={onExpand} />

        {captureInterruptedAt && <CaptureInterruptedWarning at={captureInterruptedAt} />}

        {/*
          `min-h-0` is load-bearing, not tidying. A flex child defaults to
          `min-height: auto`, so without it this pane refuses to shrink below
          its content, the column overflows, and the footer holding Copy is
          pushed off the bottom of the window — which is precisely what John
          hit on 2026-08-19: a note he could not copy. With it, Copy stays
          pinned and the note scrolls, at ANY window size.
        */}
        <div className="grow min-h-0 overflow-y-auto px-4 py-4">
          <p className="text-[13px] text-ink leading-relaxed whitespace-pre-wrap">{noteText}</p>
        </div>

        <div className="shrink-0 px-4 py-3 border-t border-border-subtle">
          <CopyButton text={noteText} onCopied={onShrink} onSettled={onCollapse} />
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen flex items-center justify-center px-4 bg-surface-secondary">
      <span className="text-xs text-ink-tertiary text-center">
        Start a recording in Claro and it will show here.
      </span>
    </div>
  )
}

function PopoutHeader({
  tone,
  title,
  onExpand,
}: {
  tone: 'ready' | 'error'
  title: string
  onExpand?: () => void
}) {
  return (
    <div className="shrink-0 flex items-center gap-2 px-4 py-3 border-b border-border-subtle">
      {tone === 'ready' ? (
        <Check className="w-4 h-4 text-claro-600 shrink-0" />
      ) : (
        <TriangleAlert className="w-4 h-4 text-error shrink-0" />
      )}
      <span className="text-sm font-semibold text-ink">{title}</span>
      {onExpand && (
        // The escape hatch for a recording stopped from the main tab: no click
        // happened in this window, so nothing has been able to resize it and
        // the note is sitting in the compact bar. Harmless when already large.
        <button
          onClick={onExpand}
          className="ml-auto shrink-0 p-1 rounded-md text-ink-tertiary hover:text-ink hover:bg-surface-tertiary transition-colors"
          title="Make this window bigger"
          aria-label="Make this window bigger"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  )
}

/**
 * A truncated note reads as complete and confident, which is exactly why the
 * schema makes surfacing this mandatory. It sits ABOVE the note rather than
 * below it: this is the window the clinician copies into the EMR from, so the
 * warning has to be read before the note, not after.
 */
function CaptureInterruptedWarning({ at }: { at: string }) {
  const time = new Date(at).toLocaleTimeString(jLocale(), { hour: 'numeric', minute: '2-digit' })
  return (
    <div
      role="alert"
      className="shrink-0 flex items-start gap-2 px-4 py-2.5 bg-warning/15 border-b border-warning/30"
    >
      <TriangleAlert className="w-4 h-4 text-warning shrink-0 mt-px" />
      <span className="text-xs font-medium text-on-warning-soft leading-relaxed">
        Recording stopped unexpectedly at {time}. This note covers the consult only up to
        then — check it before using it.
      </span>
    </div>
  )
}

function CopyButton({
  text,
  onCopied,
  onSettled,
}: {
  text: string
  /** Runs immediately after a successful copy, while the click's user
   *  activation is still live — this is where a resize has to happen. */
  onCopied?: () => void
  /** Runs after the confirmation has been read. No activation by then. */
  onSettled?: () => void
}) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      // A denied clipboard permission must not look like a successful copy —
      // the clinician would paste stale content into the record and never know.
      return
    }
    setCopied(true)
    // Shrink NOW, not on the timer: transient activation is generous but not
    // unlimited, and a resize is the one thing here that dies without it.
    onCopied?.()
    // Long enough to read the confirmation, short enough that the window is
    // out of the way before they reach the EMR field.
    setTimeout(() => {
      setCopied(false)
      onSettled?.()
    }, 900)
  }

  return (
    <button
      onClick={() => void handleCopy()}
      className="w-full flex items-center justify-center gap-2 h-11 rounded-lg bg-claro-600 hover:bg-claro-700 text-white text-sm font-semibold transition-colors"
    >
      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}
